import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

/**
 * Lightweight app-shell skeleton shown while /auth/me is validating.
 * Renders instantly — the user perceives the UI is "there" from ms 1
 * instead of a blank screen with a spinner.
 */
function AppShellSkeleton() {
  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="auth-shell-skeleton">
      {/* Sidebar placeholder — hidden on mobile */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-200 p-4 space-y-3">
        <div className="h-8 w-40 bg-slate-200 rounded animate-pulse" />
        <div className="pt-4 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />
          ))}
        </div>
      </aside>

      {/* Main area placeholder */}
      <main className="flex-1 p-4 lg:p-6 space-y-4">
        <div className="h-10 w-72 bg-slate-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="h-28 bg-white rounded-lg animate-pulse" />
          <div className="h-28 bg-white rounded-lg animate-pulse" />
          <div className="h-28 bg-white rounded-lg animate-pulse" />
        </div>
        <div className="h-64 bg-white rounded-lg animate-pulse" />
      </main>
    </div>
  );
}

export default function ProtectedRoute({ children }) {
  const { user, loading, canAccess, permissions } = useAuth();
  const location = useLocation();

  // If arriving from OAuth callback with user in state, allow through
  if (location.state?.user) return children;

  // SWR-style: if we have a cached user (from prior session), render immediately
  // while /auth/me revalidates in background. Only show skeleton on true cold-start.
  if (user) {
    // Guarda de rota: operadores só acessam módulos autorizados pelo admin.
    // Admin/superadmin => allow_all. Permissões ainda não carregadas => libera.
    const isAdmin = user.role === 'admin' || user.role === 'superadmin';
    if (!isAdmin && permissions && !permissions.allow_all) {
      const path = location.pathname;
      // Permite rotas detalhe (ex.: /os/:id) se o módulo-pai estiver liberado
      const parent = '/' + (path.split('/').filter(Boolean)[0] || '');
      if (!canAccess(path) && !canAccess(parent)) {
        if (typeof window !== 'undefined') {
          window.alert('Acesso restrito. Entre em contato com o administrador para solicitar a liberação deste módulo.');
        }
        return <Navigate to="/" replace />;
      }
    }
    return children;
  }

  if (loading) return <AppShellSkeleton />;

  return <Navigate to="/login" replace />;
}
